import React, { useState, useMemo } from "react";
import {
  Box, Card, Grid, Typography, TextField, MenuItem, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Button, InputAdornment, CircularProgress,
} from "@mui/material";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useGetShopInvoicesQuery } from "../../../Api/attoDeskApi";
import { IShopInvoice } from "../../../Api/Interface/api.interface";
import dayjs from "dayjs";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import Pagination from "../../../Components/Pagination";

const STATUS_CHIP: Record<string, { label: string; bg: string; color: string }> = {
  unpaid:  { label: "Unpaid",  bg: "#ffebee", color: "#c62828" },
  partial: { label: "Partial", bg: "#fff8e1", color: "#f57f17" },
  paid:    { label: "Paid",    bg: "#e8f5e9", color: "#2e7d32" },
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading } = useGetShopInvoicesQuery({
    searchText: debouncedSearch,
    payment_status: statusFilter !== "all" ? statusFilter : undefined,
  });

  const invoices: IShopInvoice[] = useMemo(() => {
    return Array.isArray(data?.data) ? data!.data : [];
  }, [data]);

  const totals = useMemo(() => {
    return invoices.reduce((acc, inv) => ({
      total: acc.total + inv.total,
      paid: acc.paid + inv.amount_paid,
      due: acc.due + inv.balance_due,
    }), { total: 0, paid: 0, due: 0 });
  }, [invoices]);

  if (isLoading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="30vh">
      <CircularProgress />
    </Box>
  );

  return (
    <div>
      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
        {[
          { label: "Total Invoiced", value: totals.total, color: "#1a237e" },
          { label: "Total Received", value: totals.paid, color: "#2e7d32" },
          { label: "Total Outstanding", value: totals.due, color: "#c62828" },
        ].map(c => (
          <Grid item xs={12} sm={4} key={c.label}>
            <Card sx={{ p: 2.5, borderRadius: 2, borderLeft: `4px solid ${c.color}` }}>
              <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>{c.label}</Typography>
              <Typography fontSize={22} fontWeight={800} color={c.color}>${c.value.toFixed(2)}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Card className={classes.card}>
            <Grid container justifyContent="space-between" spacing={2} sx={{ borderBottom: 2, padding: 2 }} className={classes.titleGrid}>
              <Grid item lg={3} md={4} sm={6} xs={12} alignContent="center">
                <Typography className={classes.cardTitle}>Restaurant Invoices</Typography>
              </Grid>
              <Grid item lg={9} md={8} sm={6} xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <TextField
                  select size="small" value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </TextField>
                <TextField
                  size="small" placeholder="Search invoice or restaurant..."
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 16, color: "#999" }} /></InputAdornment> }}
                  sx={{ minWidth: 260 }}
                />
              </Grid>
            </Grid>

            <TableContainer>
              <Table className={classes.table}>
                <TableHead className={classes.tableHeaderRow}>
                  <TableRow>
                    <TableCell className={classes.tablheader}><strong>#</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Invoice</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Restaurant</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Date</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Total</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Paid</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Balance</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Status</strong></TableCell>
                    <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center">No invoices found</TableCell></TableRow>
                  ) : (
                    invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((inv, i) => {
                      const s = STATUS_CHIP[inv.payment_status] || STATUS_CHIP.unpaid;
                      return (
                        <TableRow key={inv.id} className={classes.tableRowHover} sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/shop-invoice/${inv.id}`)}>
                          <TableCell className={classes.tablCell}>{page * rowsPerPage + i + 1}</TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={700} color="#1a237e">{inv.invoice_number}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13}>{(inv.shop as any)?.customerCompanyName || `Shop #${inv.shop_id}`}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={12}>{dayjs(inv.invoice_date).format("MMM D, YYYY")}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={600}>${inv.total.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={600} color="#2e7d32">${inv.amount_paid.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={600} color={inv.balance_due > 0 ? "#c62828" : "#2e7d32"}>
                              ${inv.balance_due.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Chip label={s.label} size="small" sx={{ fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.color }} />
                          </TableCell>
                          <TableCell className={classes.tablCell} sx={{ textAlign: "right" }}>
                            <Button variant="outlined" size="small"
                              onClick={(e) => { e.stopPropagation(); navigate(`/shop-invoice/${inv.id}`); }}
                              sx={{ fontSize: 11, fontWeight: 600, textTransform: "none", borderRadius: 1.5, borderColor: "#ccc", color: "#1a237e" }}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination count={invoices.length} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_: any, p: number) => setPage(p)} />
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
